import { ForwardRefRenderFunction, forwardRef } from "react";
//#region types
import { ExternalHoldingsFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import * as Yup from "yup";
import { ExternalCompanyHoldingsFormContainerProps } from "models/propTypes/externalCompanyHoldingsPropTypes";
import { WintSuperkollAPIModelDeclarations } from "wint-api-client/dist/types";
//#endregion
//#region components
import { useDeclarationService } from "serviceHooks";
import ExternalCompanyHoldingsForm from "./externalCompanyHoldingsForm";
import { PersonNumberHelper } from "utils/helpers";
//#endregion

const ExternalCompanyHoldingsFormContainer: ForwardRefRenderFunction<
  FormHandles,
  ExternalCompanyHoldingsFormContainerProps
> = (props, ref) => {
  const { declaration } = props;
  const declarationService = useDeclarationService();

  const validationSchema = Yup.object<ExternalHoldingsFormTypes.Declaration>({
    comment: Yup.string(),
    spousePersonalNumber: Yup.string()
      .test("valid-ssn-format", TranslationKeys.Validations.InvalidPersonNumber, (value) => {
        if (!value) return true;
        return PersonNumberHelper.isValidFormat(value);
      })
      .test("twelve-digits", TranslationKeys.Validations.InvalidPersonNumberFormat, (value) => {
        if (!value) return true;
        const cleaned = PersonNumberHelper.clearOtherCharacters(value);
        if (cleaned.length === 10) {
          return false;
        }
        return cleaned.length === 12;
      })
      .test("not-born-yet", TranslationKeys.Validations.PersonNumberNotBornYet, (value) => {
        if (!value) return true;
        const birthDate = PersonNumberHelper.getBirthDateFromSSN(value);
        if (!birthDate) return true;

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        birthDate.setHours(0, 0, 0, 0);
        return birthDate <= today;
      })
      .test("over-130", TranslationKeys.Validations.PersonNumberOver130, (value) => {
        if (!value) return true;
        const birthDate = PersonNumberHelper.getBirthDateFromSSN(value);
        if (!birthDate) return true;

        const age = PersonNumberHelper.calculateAge(birthDate);
        return age <= 130;
      }),
    externalHoldings: Yup.array().of(
      Yup.object<ExternalHoldingsFormTypes.ExternalHolding>({
        companyName: Yup.string().required(TranslationKeys.Validations.Required),
        ownership: Yup.number()
          .required(TranslationKeys.Validations.Required)
          .min(1, TranslationKeys.Validations.Required)
          .max(100, TranslationKeys.Validations.Required),
      })
    ),
  });

  const initialValues: ExternalHoldingsFormTypes.Declaration = {
    spousePersonalNumber: declaration?.spousePersonalNumber,
    externalHoldings: declaration?.externalHoldings ?? [],
  };

  const handleSubmit = async (values: ExternalHoldingsFormTypes.Declaration) => {
    if (!declaration) return Promise.resolve();

    const declarationData: WintSuperkollAPIModelDeclarations.Declaration = {
      ...declaration,
      comment: "",
      spousePersonalNumber: values.spousePersonalNumber,
      externalHoldings: values.externalHoldings,
    };

    return declarationService.saveDeclarationData(declarationData);
  };

  return (
    <ExternalCompanyHoldingsForm
      ref={ref}
      {...props}
      validationSchema={validationSchema}
      defaultValues={initialValues}
      handleSubmit={handleSubmit}
      onFormValidationChange={props.onFormValidationChange}
    />
  );
};

export default forwardRef(ExternalCompanyHoldingsFormContainer);
